// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a Konvo chat message's files for "Send to an agent": its attachments (a forwarded
// message's too) and the pictures its links and GIFs show, read from the media proxy like the chat.

import {type AgentSourceFile, extensionFor, uniqueSources} from '@app/features/konvo_agents/utils/AgentSourceFiles';
import type {Message} from '@app/features/messaging/models/MessagingMessage';

const PICTURE_NAME = /\.(png|jpe?g|gif|webp|bmp|avif)$/i;

async function download(url: string): Promise<Blob | null> {
	const response = await fetch(url, {credentials: 'omit'});
	return response.ok ? response.blob() : null;
}

function pictureName(url: string, type: string, count: number): string {
	try {
		const last = decodeURIComponent(new URL(url).pathname.split('/').pop() ?? '');
		if (PICTURE_NAME.test(last)) return last;
	} catch {
		// Not a URL with a name: numbered below.
	}
	return `picture-${count}.${extensionFor(type) ?? 'png'}`;
}

/** The message's attachments, then the pictures of its picture links, in the order the chat shows them. */
export function messageAgentSources(
	message: Pick<Message, 'attachments' | 'embeds' | 'messageSnapshots'>,
): Array<AgentSourceFile> {
	const attachments = [
		...message.attachments,
		...(message.messageSnapshots ?? []).flatMap((snapshot) => snapshot.attachments ?? []),
	];
	const sources: Array<AgentSourceFile> = [];
	for (const attachment of attachments) {
		const url = attachment.url ?? attachment.proxy_url;
		if (!url || attachment.expired) continue;
		sources.push({
			name: attachment.filename,
			type: attachment.content_type ?? '',
			size: attachment.size ?? null,
			load: () => download(url),
		});
	}
	const seen = new Set<string>();
	let pictures = 0;
	for (const embed of message.embeds) {
		// A GIF goes as its still: an agent reads pictures, not clips.
		if (embed.type !== 'image' && embed.type !== 'gifv') continue;
		const media = embed.image ?? embed.thumbnail;
		const url = media?.proxy_url ?? media?.url;
		if (!media || !url || seen.has(url)) continue;
		seen.add(url);
		pictures += 1;
		const type = media.content_type ?? '';
		sources.push({
			name: pictureName(media.url, type, pictures),
			type,
			size: null,
			load: () => download(url),
		});
	}
	return uniqueSources(sources);
}
